const select = (selector, scope = document) => scope.querySelector(selector);
const selectAll = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

const debounce = (fn, delay = 200) => {
  let timer = null;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

const fetchJson = async url => {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load ${url}: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

const toggleSidebar = () => {
  const sidebar = select('[data-sidebar]');
  if (!sidebar) return;
  sidebar.classList.toggle('is-open');
  document.body.classList.toggle('sidebar-open');
};

const initNavToggles = () => {
  selectAll('[data-nav-toggle]').forEach(button => {
    button.addEventListener('click', () => {
      toggleSidebar();
      const expanded = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!expanded));
    });
  });

  const collapseButton = select('[data-sidebar-toggle]');
  if (collapseButton) {
    collapseButton.addEventListener('click', () => {
      document.body.classList.toggle('sidebar-collapsed');
    });
  }
};

const initSearchOverlay = () => {
  const openButton = select('[data-open-search]');
  const closeButton = select('[data-close-search]');
  const panel = select('[data-search-panel]');
  const input = panel ? select('[data-search-input]', panel) : null;

  if (!panel) return;

  const open = () => {
    panel.hidden = false;
    panel.classList.add('is-visible');
    setTimeout(() => input?.focus(), 0);
  };

  const close = () => {
    panel.hidden = true;
    panel.classList.remove('is-visible');
  };

  openButton?.addEventListener('click', open);
  closeButton?.addEventListener('click', close);
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      close();
    }
  });
};

const initBlogCardNavigation = (scope = document) => {
  const matches = [];
  if (scope.matches?.('.blog-card[data-href]')) {
    matches.push(scope);
  }
  matches.push(...selectAll('.blog-card[data-href]', scope));

  matches.forEach(card => {
    if (card.dataset.cardNavigationInitialized === 'true') return;
    const { href } = card.dataset;
    if (!href) return;

    const navigate = () => {
      window.location.href = href;
    };

    card.addEventListener('click', event => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.target.closest('a')) return;
      navigate();
    });

    card.addEventListener('keydown', event => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (event.target.closest('a')) return;
      event.preventDefault();
      navigate();
    });

    card.dataset.cardNavigationInitialized = 'true';
  });
};
const createResultCard = item => {
  const article = document.createElement('article');
  article.className = 'blog-card';
  article.dataset.href = `/blog/${item.slug}/`;
  article.setAttribute('role', 'link');
  article.setAttribute('tabindex', '0');
  article.setAttribute('aria-label', item.title);

  const wrapper = document.createElement('div');
  wrapper.className = 'blog-card__link';

  const imageWrapper = document.createElement('div');
  imageWrapper.className = 'blog-card__image';
  const image = document.createElement('img');
  image.src = item.coverImage;
  image.alt = item.title;
  image.loading = 'lazy';
  imageWrapper.appendChild(image);
  wrapper.appendChild(imageWrapper);

  const content = document.createElement('div');
  content.className = 'blog-card__content';
  content.innerHTML = `
    <h3 class="blog-card__title">${item.title}</h3>
    <p class="blog-card__excerpt">${item.excerpt || ''}</p>
    <div class="blog-card__meta">
      <div class="blog-card__author">
        <span class="blog-card__author-name">${item.author?.name || 'Admin'}</span>
      </div>
      <div class="blog-card__info">
        <span class="blog-card__reading-time">${item.readingTime} min read</span>
      </div>
    </div>
  `;
  wrapper.appendChild(content);
  article.appendChild(wrapper);

  if (Array.isArray(item.categories) && item.categories.length) {
    const topics = document.createElement('div');
    topics.className = 'blog-card__topics-list';
    item.categories.slice(0, 2).forEach((name, index) => {
      const slug = item.categorySlugs?.[index];
      const node = document.createElement(slug ? 'a' : 'span');
      node.className = 'blog-card__topic';
      node.textContent = name;
      if (slug) node.href = `/categories/${slug}/`;
      topics.appendChild(node);
    });
    article.appendChild(topics);
  }

  if (Array.isArray(item.tags) && item.tags.length) {
    const tagList = document.createElement('ul');
    tagList.className = 'blog-card__tags-list';
    item.tags.slice(0, 3).forEach((name, index) => {
      const slug = item.tagSlugs?.[index];
      const li = document.createElement('li');
      const node = document.createElement(slug ? 'a' : 'span');
      node.className = 'blog-card__tag';
      node.textContent = name;
      if (slug) node.href = `/tags/${slug}/`;
      li.appendChild(node);
      tagList.appendChild(li);
    });
    article.appendChild(tagList);
  }

  initBlogCardNavigation(article);
  return article;
};


const initSearchPage = async () => {
  const container = select('[data-search-page]');
  if (!container) return;

  const form = select('[data-search-form]', container);
  const input = select('[data-search-query]', container);
  const resultsGrid = select('[data-search-results]', container);
  const summary = select('[data-search-summary]', container);
  const emptyState = select('[data-search-empty]', container);
  const emptyDefault = select('[data-empty-default]', container);
  const emptyNoResults = select('[data-empty-noresults]', container);
  const emptyTerm = select('[data-search-term]', container);

  let dataset = [];
  try {
    dataset = await fetchJson('/data/blog-search.json');
  } catch (err) {
    console.error('[search] failed to load dataset', err);
    if (summary) summary.textContent = '暂时无法加载文章数据。';
    return;
  }

  const render = items => {
    resultsGrid.innerHTML = '';
    if (!items.length) {
      emptyState?.removeAttribute('hidden');
      return;
    }

    emptyState?.setAttribute('hidden', 'hidden');
    const fragment = document.createDocumentFragment();
    items.forEach(item => fragment.appendChild(createResultCard(item)));
    resultsGrid.appendChild(fragment);
    initBlogCardNavigation(resultsGrid);
  };

  const update = () => {
    const query = input.value.trim().toLowerCase();
    if (!query) {
      if (summary) summary.textContent = '输入关键词即可搜索文章标题。';
      emptyDefault?.removeAttribute('hidden');
      emptyNoResults?.setAttribute('hidden', 'hidden');
      render([]);
      return;
    }

    const matches = dataset.filter(item => {
      const title = String(item.title || '').toLowerCase();
      const matchTitle = title.includes(query);
      const matchTags = Array.isArray(item.tags) && item.tags.some(tag => String(tag).toLowerCase().includes(query));
      const matchCategories =
        Array.isArray(item.categories) && item.categories.some(cat => String(cat).toLowerCase().includes(query));
      return matchTitle || matchTags || matchCategories;
    });

    if (summary) {
      summary.textContent = matches.length
        ? `找到 ${matches.length} 篇文章，关键词 “${query}”`
        : `没有找到与 “${query}” 匹配的文章。`;
    }

    if (!matches.length) {
      emptyDefault?.setAttribute('hidden', 'hidden');
      emptyNoResults?.removeAttribute('hidden');
      if (emptyTerm) emptyTerm.textContent = query;
    } else {
      emptyDefault?.setAttribute('hidden', 'hidden');
      emptyNoResults?.setAttribute('hidden', 'hidden');
    }

    render(matches);
  };

  const debouncedUpdate = debounce(update, 150);

  input.addEventListener('input', debouncedUpdate);
  form?.addEventListener('submit', event => {
    event.preventDefault();
    update();
  });

  update();
};

const initAnchorShortcut = () => {
  document.addEventListener('keydown', event => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      const focusTarget = select('#global-search') || select('#search-query');
      focusTarget?.focus();
    }
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initNavToggles();
  initSearchOverlay();
  initSearchPage();
  initAnchorShortcut();
  initBlogCardNavigation();
});
